import {
  keyDateRanges,
  keyDateUsage,
  MAX_KEY_DATES,
  type KeyDate,
  type KeyDateId,
  type MonthIndex,
} from '@finsim/model';
import { useUnit } from 'effector-react';
import { Plus, Trash2 } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { $project, commandIssued } from '../../entities/project';
import { newId } from '../../shared/lib/id';
import {
  Button,
  EditOnly,
  IconButton,
  MonthField,
  Section,
  TextField,
  Tooltip,
} from '../../shared/ui';

/**
 * Named months (e.g. "Building finished"). Other dates can be tied to them, so moving one moves
 * everything tied to it.
 */
export function KeyDatesSection() {
  const { t } = useTranslation();
  const [project, issue] = useUnit([$project, commandIssued]);
  const usage = useMemo(
    () => (project ? keyDateUsage(project) : new Map<KeyDateId, number>()),
    [project],
  );
  // Months that keep every date tied to each key date valid.
  const ranges = useMemo(
    () => (project ? keyDateRanges(project) : new Map<KeyDateId, [number, number]>()),
    [project],
  );
  if (!project) return null;
  const { start, horizonMonths } = project.assumptions;
  const update = (keyDate: KeyDate, field: string): void => {
    issue({ command: { type: 'keyDate/update', keyDate }, coalesceKey: `${keyDate.id}:${field}` });
  };

  return (
    <Section
      title={t('keyDates.title')}
      actions={
        <EditOnly>
          {project.keyDates.length < MAX_KEY_DATES && (
            <Button
              size="sm"
              variant="ghost"
              leadingIcon={<Plus />}
              onClick={() => {
                const keyDate: KeyDate = {
                  id: newId('kd') as KeyDateId,
                  name: t('keyDates.newName', { n: project.keyDates.length + 1 }),
                  month: Math.min(12, horizonMonths - 1) as MonthIndex,
                };
                issue({ command: { type: 'keyDate/add', keyDate } });
              }}
            >
              {t('keyDates.add')}
            </Button>
          )}
        </EditOnly>
      }
    >
      <p className="text-xs text-text-3">{t('keyDates.description')}</p>
      {project.keyDates.map((keyDate) => {
        const tied = usage.get(keyDate.id) ?? 0;
        const range = ranges.get(keyDate.id) ?? [0, horizonMonths - 1];
        return (
          <fieldset
            key={keyDate.id}
            aria-label={keyDate.name}
            className="space-y-1.5 rounded-md border border-border-subtle p-2.5"
          >
            <div className="flex items-center gap-1.5">
              <TextField
                aria-label={t('keyDates.name')}
                className="min-w-0 flex-1"
                maxLength={80}
                value={keyDate.name}
                onCommit={(name) => {
                  update({ ...keyDate, name }, 'name');
                }}
              />
              <EditOnly>
                <Tooltip content={t('keyDates.remove', { name: keyDate.name })}>
                  <IconButton
                    size="md"
                    label={t('keyDates.remove', { name: keyDate.name })}
                    onClick={() => {
                      issue({ command: { type: 'keyDate/remove', id: keyDate.id } });
                    }}
                  >
                    <Trash2 />
                  </IconButton>
                </Tooltip>
              </EditOnly>
            </div>
            <MonthField
              aria-label={`${keyDate.name} — ${t('keyDates.month')}`}
              value={keyDate.month}
              start={start}
              min={range[0]}
              max={range[1]}
              onCommit={(month) => {
                update({ ...keyDate, month }, 'month');
              }}
            />
            <p className="text-xs text-text-3">
              {tied === 0 ? t('keyDates.untied') : t('keyDates.tied', { count: tied })}
            </p>
            {keyDate.month >= horizonMonths && (
              <p className="text-xs text-text-2">{t('keyDates.afterHorizon')}</p>
            )}
          </fieldset>
        );
      })}
    </Section>
  );
}
